import './CarouselTicker.scss';

import {
  HomefieldIconChevronLeft,
  HomefieldIconChevronRight,
  HomefieldIconMediaPause,
  HomefieldIconMediaPlay,
} from '@dsg-tech/homefield-react-icons';
/* eslint-disable react/jsx-no-target-blank */
import { startTransition, useCallback, useEffect, useState } from 'react';

import { checkStores } from 'utility/utility';
import { multiUseAnalyticsEvent } from 'utility/analytics-events';
import useInterval from 'hooks/useInterval';
import { TickerData } from '../Ticker.interface';
import { generateDataEm } from '../Ticker.utils';

interface CarouselTickerProps {
  tickerData: TickerData[];
}

const CarouselTicker: React.FC<CarouselTickerProps> = ({ tickerData }) => {
  const slidesToScrollNext = 1;
  const slidesToScrollPrev = -1;
  const [duration, setDuration] = useState(11000);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState('black');
  const [foregroundColor, setForegroundColor] = useState('white');
  const [sliding, setSliding] = useState(true);
  const [slideWidth, setSlideWidth] = useState('768px');
  const [initialized, setInitialized] = useState(false);
  const [hasTickerAnalyticsFired, setHasTickerAnalyticsFired] = useState(false);

  const resetIntervalHook = () => {
    setDuration((prevDuration) => (prevDuration % 2 === 1 ? 11000 : 11001));
  };

  const handleNext = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>, interval = false) => {
      if (!interval) resetIntervalHook();
      if (sliding || !interval) {
        setCurrentSlide(
          (prevSlide) => (prevSlide + slidesToScrollNext + tickerData.length) % tickerData.length,
        );
      }
    },
    [sliding, tickerData.length],
  );

  useEffect(() => {
    if (tickerData.length > 0) {
      setBackgroundColor(tickerData[currentSlide].backgroundColor ?? 'black');
      setForegroundColor(tickerData[currentSlide].foregroundColor ?? 'white');
    }
  }, [currentSlide, tickerData]);

  const handlePrevious = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>, interval = false) => {
      if (!interval) resetIntervalHook();
      setCurrentSlide(
        (prevSlide) => (prevSlide + slidesToScrollPrev + tickerData.length) % tickerData.length,
      );
    },
    [tickerData.length],
  );

  useInterval(() => handleNext(undefined, true), sliding && initialized ? duration : null);

  const handleResize = useCallback(() => {
    const tickerItem = document.querySelector('.rh-ticker-carousel-item');
    setSlideWidth(tickerItem ? `${tickerItem.getBoundingClientRect().width}px` : 'auto');
  }, []);

  // Effect for handling window resizes to update slide width
  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    startTransition(() => {
      setCurrentSlide(0);
      setInitialized(true);
    });
  }, [tickerData]);

  const carouselOffset = `-${currentSlide * parseInt(slideWidth, 10)}px`;

  const fireTickerAnalytics = () => {
    if (!hasTickerAnalyticsFired) {
      multiUseAnalyticsEvent({ event: 'tickerFirstInteraction' });
      setHasTickerAnalyticsFired(true);
    }
  };

  return (
    <div
      className="rh-ticker-carousel-wrapper header-ticker-minimum hmf-display-flex hmf-flex-row hmf-justify-content-center hmf-position-relative"
      style={{ background: backgroundColor ?? 'black' }}>
      <div
        className="rh-ticker-carousel hmf-display-flex hmf-flex-row hmf-align-items-center hmf-justify-content-center hmf-text-align-center"
        aria-labelledby="carousel-ticker">
        <div className="rh-ticker-carousel-control--spacer" />
        <button
          type="button"
          onClick={() => {
            handlePrevious();
            // Remove analytics event once test is complete
            fireTickerAnalytics();
          }}
          aria-label="previous carousel slide">
          <HomefieldIconChevronLeft width="24px" height="24px" />
        </button>
        <div
          id="carousel-ticker"
          className="rh-ticker-carousel-track hmf-display-block"
          style={{ width: slideWidth }}>
          <div
            className="rh-ticker-carousel-inner hmf-display-flex hmf-flex-row hmf-align-self-center hmf-m-0 hmf-p-0"
            style={{
              transition: 'transform 250ms ease-in',
              transform: `translateX(${carouselOffset})`,
            }}>
            {tickerData.map((ticker, index) => {
              // In order to be able to catch the error with ErrorBoundaryWithLogger and to avoid the component from breaking
              // We need to catch the error in try-catch and still wrap with ErrorBoundaryWithLogger
              try {
                return (
                  <div
                    key={`ticker-${ticker.linkTitle}-${ticker.topText}-${ticker.link}`}
                    className="rh-ticker-carousel-item hmf-display-flex"
                    aria-hidden={currentSlide !== index}>
                    <div className="rh-ticker-carousel-content-section hmf-p-xxs">
                      <a
                        href={checkStores(ticker.link)}
                        target={checkStores(ticker.link)?.includes('http') ? '_blank' : '_self'}
                        style={{ color: foregroundColor ?? 'white' }}
                        tabIndex={currentSlide === index ? 0 : -1}
                        data-em={generateDataEm(ticker)}
                        className="hmf-display-flex hmf-flex-column hmf-text-decoration-none">
                        <span className="rh-ticker-carousel-top-section hmf-body-bold-s">
                          {ticker.topText}
                        </span>
                        <span className="rh-ticker-carousel-bottom-section hmf-legal-s">
                          {ticker.bottomText}{' '}
                          <span className="rh-ticker-carousel-link hmf-legal-s hmf-text-decoration-underline">
                            {ticker.linkTitle}
                          </span>
                        </span>
                      </a>
                    </div>
                  </div>
                );
              } catch (e) {
                return null;
              }
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            handleNext();
            // Remove analytics event once test is complete
            fireTickerAnalytics();
          }}
          aria-label="next carousel slide"
          alt="Next Carousel Slide">
          <HomefieldIconChevronRight width="24px" height="24px" />
        </button>
        <div className="rh-ticker-play-pause">
          <button
            type="button"
            onClick={() => {
              setSliding((prev) => !prev);
              // Remove analytics event once test is complete
              fireTickerAnalytics();
            }}
            aria-label={sliding ? 'carousel pause' : 'carousel play'}
            alt={sliding ? 'Carousel Control: Pause' : 'Carousel Control: Play'}>
            {sliding ? (
              <HomefieldIconMediaPause width="24px" height="29px" />
            ) : (
              <HomefieldIconMediaPlay width="24px" height="24px" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarouselTicker;
